import { createContext, FC, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FilterType, RegionSetStatusType } from "@app/projects/enumerators";
import { RegionGranularity } from "@app/region-sets";
import ProjectService from "@app/projects/projectService";
import useCleanHistory from "@app/shared/useCleanHistory";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import GeographyService from "@app/geography/geographyService";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from '@app/feature-flags/features';

type ServiceArea = {
  displayName: string;
  regionCode: string;
  regionGranularity: RegionGranularity;
  regionSetId: number;
  subRegions: {
    displayName: string;
    regionCode: string;
    regionGranularity: RegionGranularity;
    regionSetId: number;
    status: RegionSetStatusType;
  }[],
}

interface ProviderSourceItem {
  name: string;
  type?: ProviderNetworkTypeEnum;
  children?: { name: string; type: ProviderNetworkTypeEnum }[];
}

interface FilterContextProps {
  effectivenessEfficiencyValues: number[];
  getCurrentServiceAreaStatus: () => RegionSetStatusType;
  getSpecialtyTooltipText: () => string;
  isFilterActive: boolean;
  mapButtonsAreDisabled: boolean;
  mappedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[];
  providerSource: ProviderSourceItem[];
  replaceFilterUrl: (filterType: FilterType, currentValue: string, newValue: string) => void;
  serviceArea: ServiceArea[];
  selectedProviderSources: ProviderNetworkTypeEnum[];
  selectedServiceArea: string;
  selectedServiceAreas: string[];
  selectedSpecialtyGroupCode: DropdownItem<string>;
  selectedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[];
  serviceAreaRegionIdToRegionCodeMap: Map<string, { countyCode: string, stateCode: string }>;
  setIsFilterActive: (active: boolean) => void,
  setSelectedProviderSources: (providerSource: ProviderNetworkTypeEnum[]) => void;
  setSelectedServiceArea: (serviceArea: string) => void;
  setSelectedServiceAreas: (serviceArea: string[]) => void;
  setSelectedSpecialtyGroupCode: (specialtyGroupCode: DropdownItem<string>) => void;
  setSelectedVolumeIndicators: (volumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[]) => void;
  sortedSpecialtyGroupCodes: DropdownItem<string>[];
  adequacyTimeslices: ProjectRunConfiguration[];
  selectedTimeslice: number;
  handleSelectedTimesliceChange: (id: number) => void;
  countyStatusOptions: { key: string; name: string }[];
  selectedCountyStatuses: RegionSetStatusType[];
  setSelectedCountyStatuses: (statuses: RegionSetStatusType[]) => void;
  availableCountyStatuses: RegionSetStatusType[];
  getDefaultSelectedStatuses: (statuses: RegionSetStatusType[]) => RegionSetStatusType[];
  resetCountyStatuses: () => void;
  showAdvancedComparison: boolean;
}

const FilterContext = createContext<Partial<FilterContextProps>>({
  availableCountyStatuses: [],
});

const FilterContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const projectContext = useContext(CountyProjectContext);
  const projectService = useRef(new ProjectService()).current;
  const georgraphyService = useRef(new GeographyService()).current;
  const pushOrReplaceLocation = useCleanHistory();
  const adequacyTimeslices = projectContext.project.projectRunConfigurations;
  const showAdvancedComparison = projectContext.project.showAdvancedComparison;

  const history = useHistory();
  const location = useLocation();

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const serviceAreaWideMapping = featureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY);

  // Filter Options
  const [mapButtonsAreDisabled, setMapButtonsAreDisabled] = useState(false);
  const [selectedServiceArea, setSelectedServiceArea] = useState<string>('');
  const [selectedServiceAreas, setSelectedServiceAreas] = useState<string[]>([]);
  const latestProjectFilters = useRef(projectContext.filters);
  const [selectedSpecialtyGroupCode, setSelectedSpecialtyGroupCode] = useState<DropdownItem<string>>({ key: '', name: '' });
  const [selectedProviderSources, setSelectedProviderSources] = useState<ProviderNetworkTypeEnum[]>([ProviderNetworkTypeEnum.Network]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [serviceArea, setServiceArea] = useState<ServiceArea[]>([]);
  const serviceAreaRegionIdToRegionCodeMap = useRef<Map<string, { countyCode: string, stateCode: string }>>(new Map());
  const [selectedTimeslice, setSelectedTimeslice] = useState<number>(projectContext.runConfiguration?.projectRunConfigurationId ?? null);

  const [availableCountyStatuses, setAvailableCountyStatuses] = useState<RegionSetStatusType[]>([]);
  const [selectedCountyStatuses, setSelectedCountyStatuses] = useState<RegionSetStatusType[]>([]);

  const [selectedVolumeIndicators, setSelectedVolumeIndicators] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>([]);

  const getDefaultSelectedStatuses = (statuses: RegionSetStatusType[]): RegionSetStatusType[] => {
    const mainStatuses = [
      RegionSetStatusType.Active,
      RegionSetStatusType.Pending,
      RegionSetStatusType.Approved
    ];
    const hasMainStatus = statuses.some(s => mainStatuses.includes(s));
    if (hasMainStatus) {
      return statuses.filter(s => mainStatuses.includes(s));
    }
    if (statuses.includes(RegionSetStatusType.Opportunity)) {
      return [RegionSetStatusType.Opportunity];
    }
    return [];
  };

  const getCurrentServiceAreaStatus = (): RegionSetStatusType => {
    const selectedRegionSetId = serviceAreaWideMapping
      ? selectedServiceAreas.find(regionSetId => !!regionSetId)
      : selectedServiceArea;

    const county = serviceAreaRegionIdToRegionCodeMap.current.get(selectedRegionSetId);
    if (!county) return RegionSetStatusType.Unknown;

    const s = serviceArea.find(sa => sa.subRegions.some(sr => sr.regionCode === county.countyCode));

    if (!s) return RegionSetStatusType.Unknown;

    const subRegion = s.subRegions.find(sr => sr.regionCode === county.countyCode);

    if (!subRegion) return RegionSetStatusType.Unknown;

    return subRegion.status || RegionSetStatusType.Unknown;
  }

  const providerSource: ProviderSourceItem[] = [
    { name: 'Network Servicing Providers', type: ProviderNetworkTypeEnum.Network },
  ];

  const outOfNetworkOptions: { name: string, type: ProviderNetworkTypeEnum }[] = [];

  if (!!projectContext.runConfiguration?.prospectProviderSetId) {
    outOfNetworkOptions.push({ name: 'Prospect Providers', type: ProviderNetworkTypeEnum.Prospect });
  }
  if (!!projectContext.runConfiguration?.marketProviderSetId) {
    outOfNetworkOptions.push({ name: 'Market Providers', type: ProviderNetworkTypeEnum.Market });
  }

  if (outOfNetworkOptions.length > 0) {
    providerSource.push({
      children: outOfNetworkOptions,
      name: 'Out of Network Providers',
    });
  }

  const sortedSpecialtyGroupCodes: DropdownItem<string>[] = (projectContext.runConfiguration?.specialtyGroups || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(sg => {
      return {
        key: sg.code,
        name: sg.name
      };
    });

  const mappedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[] = Object.values(ClaimsActivityVolumeLabelEnum)
    .filter(item => item !== ClaimsActivityVolumeLabelEnum.Total)
    .map(item => ({
      key: item as ClaimsActivityVolumeLabelEnum,
      name: item.toString()
    }));

  const effectivenessEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum)
    .map(v => Number(v))
    .filter(v => !isNaN(v)) as number[];

  const handleSelectedTimesliceChange = (id: number) => {
    setSelectedTimeslice(id);
  };

  const getSpecialtyTooltipText = (): string => {
    const requiredLengthForTooltip = 45;
    const specialtyName = sortedSpecialtyGroupCodes.find(sg => sg.key === selectedSpecialtyGroupCode.key)?.name || "";

    if (specialtyName.length >= requiredLengthForTooltip) {
      return specialtyName;
    } else {
      return "";
    }
  }

  const replaceFilterUrl = (filterType: FilterType, currentValue: string, newValue: string) => {
    let { pathname } = history.location;
    if (pathname.includes(filterType)) {
      pathname = pathname.replaceAll(filterType + currentValue, "");
      const pathParameter = getPathParameter(history.location.pathname, filterType.replace(/\//g, ""));
      if (pathParameter) {
        pathname = pathname.replaceAll(filterType + pathParameter, "");
      }
    }
    if (newValue) {
      pathname = pathname.concat(filterType + newValue)
    }
    if (pathname !== history.location.pathname) {
      pushOrReplaceLocation({ pathname });
      projectContext.loadedFilters = false;
    }
  }

  const getPathParameter = (path: string, param: string): string => {
    const pathSplit = path.split('/');
    const paramIndex = pathSplit.indexOf(param);

    if (paramIndex > -1 && paramIndex < pathSplit.length - 1) return pathSplit[paramIndex + 1];
  }

  const getFilterValue = (filters: RegionFilterItem[], filterTypeValue: FilterType): string => {
    const filterItem = filters.find(filter => filter.filterType === filterTypeValue);

    if (!!filterItem) return filterItem.filterValue;

    // If no county filter is found, it might still be in the url.  Attempt to parse it from the url.
    const filterPathValue = getPathParameter(location?.pathname, filterTypeValue.replace(/\//g, ""));

    if (!filterPathValue) return '';
    return filterPathValue;
  };

  const getCountyFilterValues = (filters: RegionFilterItem[]): string[] => {
    const countyFilterValues = filters.filter(f => f.filterType === FilterType.County).map(f => f.filterValue);

    //Since the value is a county code, we have to dig for the region set id with our code
    return countyFilterValues
      .map(v => [...serviceAreaRegionIdToRegionCodeMap.current].find(([, value]) => value.countyCode === v)?.[0])
      .filter(id => !!id);
  };

  const resetCountyStatuses = () => {
    setSelectedCountyStatuses(getDefaultSelectedStatuses(availableCountyStatuses));
  };

  const applyCountyFilterToServiceArea = (filters: RegionFilterItem[]) => {
    const countyRegionSetIds = getCountyFilterValues(filters);

    if (!serviceAreaWideMapping) {
      setSelectedServiceArea(countyRegionSetIds[0] ?? '');
      return;
    }

    if (countyRegionSetIds.length === 0) {
      setSelectedServiceArea('');
      setSelectedServiceAreas([]);
      return;
    }

    setSelectedServiceArea(countyRegionSetIds[0]);
    setSelectedServiceAreas(countyRegionSetIds);
  };

  const filteredServiceAreas = selectedCountyStatuses.length === 0
    ? []
    : serviceArea.map(state => ({
      ...state,
      subRegions: state.subRegions.filter(sr => selectedCountyStatuses.includes(sr.status))
    })).filter(state => state.subRegions.length > 0);

  useEffect(() => {
    let cancelled = false;

    // Get all the counties for the selected timeslice
    const getServiceAreas = async () => {
      // Use selectedTimeslice for the run configuration id
      const runConfigId = selectedTimeslice ?? projectContext.runConfiguration?.projectRunConfigurationId;
      if (!runConfigId)
        return;

      const countiesByState = await projectService.getCountiesByRunConfigurationId(
        projectContext.project.projectId.toString(),
        runConfigId?.toString(),
        false
      );

      const statesList = await georgraphyService.getStatesForDroplist();

      const availableStatuses = await projectService.getAvailableStatuses(
        projectContext.project.projectId,
        runConfigId
      );

      if (cancelled) {
        return;
      }

      const uniqueStatuses = Array.from(new Set(
        availableStatuses.flatMap(s => s.availableStatuses || [])
      ));
      setAvailableCountyStatuses(uniqueStatuses);

      const serviceAreas: ServiceArea[] = countiesByState.map(state => {
        const stateInfo = availableStatuses.find(s => s.regionCode === state.key);
        // State name in `state` is the abbreviation, so we need to find the full name
        const stateName = statesList.find(s => s.abbreviation === state.key)?.name || state.name;

        return {
          displayName: stateName,
          regionCode: state.name,
          regionGranularity: stateInfo?.regionGranularity,
          regionSetId: stateInfo?.regionSetId,
          subRegions: state.children.map(county => {
            const countyInfo = availableStatuses.find(s => s.regionSetId === Number(county.key));
            if (!countyInfo) {
              return;
            }

            serviceAreaRegionIdToRegionCodeMap.current.set(
              county.key,
              {
                countyCode: countyInfo.regionCode,
                stateCode: state.key
              });

            return {
              displayName: county.name,
              regionCode: countyInfo.regionCode,
              regionGranularity: countyInfo.regionGranularity,
              regionSetId: countyInfo.regionSetId,
              status: countyInfo.availableStatuses ? countyInfo.availableStatuses[0] : RegionSetStatusType.Unknown,
            };
          }).filter(sr => !!sr), // Filter out any undefined sub-regions
        }
      });

      setServiceArea(serviceAreas);
    }

    getServiceAreas().then(() => {
      if (cancelled) {
        return;
      }

      const _key = getFilterValue(latestProjectFilters.current, FilterType.Specialty);
      setSelectedSpecialtyGroupCode({ key: _key, name: sortedSpecialtyGroupCodes.find(sg => sg.key === _key)?.name || '' });
      applyCountyFilterToServiceArea(latestProjectFilters.current);
      setSelectedProviderSources([ProviderNetworkTypeEnum.Network]);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedTimeslice, projectContext.project?.projectId]);

  useEffect(() => {
    latestProjectFilters.current = projectContext.filters;
    const _key = getFilterValue(projectContext.filters, FilterType.Specialty);
    setSelectedSpecialtyGroupCode({ key: _key, name: sortedSpecialtyGroupCodes.find(sg => sg.key === _key)?.name || '' });
    applyCountyFilterToServiceArea(projectContext.filters);
  }, [projectContext.filters]);

  useEffect(() => {
    const serviceArea = serviceAreaWideMapping ? selectedServiceAreas.length !== 0 : selectedServiceArea;
    setMapButtonsAreDisabled(!(!!serviceArea && !!selectedSpecialtyGroupCode.key && !!selectedProviderSources.length && !!selectedCountyStatuses.length));
  }, [selectedServiceArea, selectedServiceAreas, selectedSpecialtyGroupCode, selectedProviderSources, selectedCountyStatuses]);

  useEffect(() => {
    const pathname = history.location.pathname;
    const replacedPathname = pathname.replace(/\/+$/, "");
    pushOrReplaceLocation({ pathname: replacedPathname });
  }, [history.location.pathname]);

  useEffect(() => {
    if (!!projectContext?.runConfiguration?.projectRunConfigurationId && selectedTimeslice !== projectContext.runConfiguration?.projectRunConfigurationId) {
      setSelectedTimeslice(projectContext.runConfiguration.projectRunConfigurationId)
    }
  }, [projectContext.runConfiguration?.projectRunConfigurationId]);

  useEffect(() => {
    if (availableCountyStatuses.length === 0) {
      setSelectedCountyStatuses([]);
      return;
    }

    const getRelevantStatusKeysForQuery = availableCountyStatuses.filter(status =>
      [
        RegionSetStatusType.Opportunity,
        RegionSetStatusType.Active,
        RegionSetStatusType.Approved,
        RegionSetStatusType.Pending
      ].includes(status)
    );

    const params = new URLSearchParams(window.location.search);

    const activeStatusesFromQuery = getRelevantStatusKeysForQuery.filter(
      key => params.get(key.toLowerCase()) === "true"
    );

    // If only Opportunity is active and available, select it
    if (
      activeStatusesFromQuery.length === 1 &&
      activeStatusesFromQuery[0] === RegionSetStatusType.Opportunity
    ) {
      setSelectedCountyStatuses([RegionSetStatusType.Opportunity]);
    } else if (activeStatusesFromQuery.length > 0) {
      setSelectedCountyStatuses(activeStatusesFromQuery);
    } else {
      setSelectedCountyStatuses(getDefaultSelectedStatuses(availableCountyStatuses));
    }
  }, [availableCountyStatuses, projectContext.filters]);

  // Sync selectedCountyStatuses to the URL when they change
  useEffect(() => {
    if (availableCountyStatuses.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    [
      RegionSetStatusType.Opportunity,
      RegionSetStatusType.Active,
      RegionSetStatusType.Approved,
      RegionSetStatusType.Pending
    ].forEach(status => {
      urlParams.set(status.toLowerCase(), selectedCountyStatuses.includes(status) ? "true" : "false");
    });

    if (window.location.search !== `?${urlParams.toString()}`) {
      history.replace({ search: urlParams.toString() });
    }
  }, [selectedCountyStatuses, availableCountyStatuses, history]);

  const contextValue: FilterContextProps = {
    adequacyTimeslices,
    availableCountyStatuses,
    countyStatusOptions,
    effectivenessEfficiencyValues,
    getCurrentServiceAreaStatus,
    getDefaultSelectedStatuses,
    getSpecialtyTooltipText,
    handleSelectedTimesliceChange,
    isFilterActive,
    mapButtonsAreDisabled,
    mappedVolumeIndicators,
    providerSource,
    replaceFilterUrl,
    resetCountyStatuses,
    selectedCountyStatuses,
    selectedProviderSources,
    selectedServiceArea,
    selectedServiceAreas,
    selectedSpecialtyGroupCode,
    selectedTimeslice,
    selectedVolumeIndicators,
    serviceArea: filteredServiceAreas,
    serviceAreaRegionIdToRegionCodeMap: serviceAreaRegionIdToRegionCodeMap.current,
    setIsFilterActive,
    setSelectedCountyStatuses,
    setSelectedProviderSources,
    setSelectedServiceArea,
    setSelectedServiceAreas,
    setSelectedSpecialtyGroupCode,
    setSelectedVolumeIndicators,
    showAdvancedComparison,
    sortedSpecialtyGroupCodes,
  };

  return (
    <FilterContext value={contextValue}>
      {props.children}
    </FilterContext>
  );
};

export { FilterContext, FilterContextProvider, FilterContextProps };
export const countyStatusOptions = Object.values(RegionSetStatusType)
  .filter(v => v !== RegionSetStatusType.Unknown && v !== RegionSetStatusType.Opportunity)
  .map(v => ({ key: v, name: v }));